/**
 * The object model.
 *
 * One sentence decides the whole shape of this file: **two objects in one
 * collection may carry different properties, and that is the normal case**
 * (decision 0014). So there is no table, no row, and no schema to migrate — an
 * object is a bag of slots keyed by property key, and a collection is a query.
 */

import type { Role, RoleKind } from '@morph/spec';
import type { ObjectId, PropertyKeyId, Revision } from './ids.js';
import type { Value } from './value.js';

/** `absent` is not `empty`. A column with holes and no column at all are
 *  different layouts, and only this distinction tells them apart. */
export type PropertyState = 'absent' | 'empty' | 'set';

export type PropertySlot =
  | { readonly state: 'set'; readonly value: Value; readonly updatedAt: string }
  | { readonly state: 'empty'; readonly updatedAt: string };

export interface StoredObject {
  readonly id: ObjectId;
  readonly revision: Revision;
  readonly typeHint?: string;                                  // correctable hint, never a contract
  readonly slots: ReadonlyMap<PropertyKeyId, PropertySlot>;    // the per-object property SET
  readonly roles: ReadonlyMap<Role, readonly Value[]>;         // manifest-resolved; >1 iff >1 key
  readonly deletedAt?: string;
}

export function stateOf(o: StoredObject, key: PropertyKeyId): PropertyState {
  return o.slots.get(key)?.state ?? 'absent';
}

export interface PropertyKeyRecord {
  readonly id: PropertyKeyId;
  readonly label: string;            // private content; never returned in a shape, never pooled
  readonly kind: RoleKind;
  readonly roleHint?: Role;
  readonly createdAt: string;
  readonly retiredAt?: string;
}

export interface PropertyDraft {
  readonly label: string;
  readonly kind: RoleKind;
  readonly roleHint?: Role;
}

export interface ObjectDraft {
  readonly id?: ObjectId;
  readonly typeHint?: string;
  readonly properties?: readonly (readonly [PropertyKeyId, Value])[];
}

/** One entry of the private slot map. The map itself never leaves the store and
 *  never enters a spec or the pool — `bindingManifestRef` in the spec is an
 *  opaque handle to it, which is the invariant the whole pool safety argument
 *  rests on. */
export interface ManifestEntry {
  readonly role: Role;
  readonly key: PropertyKeyId;
}
